import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from "react-router-dom";
import "./layouts.css";

function BookingDeptRedirect() {
  const { department } = useParams();
  const deptLower = (department || '').toLowerCase();
  if (['hotel', 'hospital', 'salon', 'cab'].includes(deptLower)) {
    return <Navigate to={`/user/booking/${deptLower}`} replace />;
  }
  return <Navigate to={`/user/booking/${department}`} replace />;
}

function BookingItemRedirect() {
  const { department, id } = useParams();
  const deptLower = (department || '').toLowerCase();
  if (['hotel', 'hospital', 'salon', 'cab'].includes(deptLower)) {
    return <Navigate to={`/user/booking/${deptLower}/${id}`} replace />;
  }
  return <Navigate to={`/user/booking/${department}/${id}`} replace />;
}

// STYLISH PREMIUM LOADER
import PremiumLoading from "./components/common/PremiumLoading";

// STATIC HELPERS (Keep static to avoid delays)
import LandingPageController from "./components/landing/LandingPageController";
import GlobalNotificationHandler from "./components/common/GlobalNotificationHandler";
import AuthGuard from "./components/common/AuthGuard";
import FeatureGuard from "./components/common/FeatureGuard";
import ChatBot from "./components/common/ChatBot";

// DYNAMIC LAZY IMPORTS (Code Splitting)
// LOGIN & REGISTER
const SignIn = lazy(() => import("./components/SignIn/SignIn"));
const UserRegister = lazy(() => import("./components/SignIn/UserRegister"));
const UserLogin = lazy(() => import("./components/SignIn/UserLogin"));
const HostRegister = lazy(() => import("./components/SignIn/HostRegister"));
const HostLogin = lazy(() => import("./components/SignIn/HostLogin"));
const AdminLogin = lazy(() => import("./components/SignIn/AdminLogin"));
const EmployeeLogin = lazy(() => import("./components/SignIn/EmployeeLogin"));
const ForgotPassword = lazy(() => import("./components/SignIn/ForgotPassword"));

// DASHBOARDS
const UserDashboard = lazy(() => import("./pages/UserDashboard/UserDashboard"));
const UserHome = lazy(() => import("./pages/UserDashboard/UserHome"));
const UserMainDashboard = lazy(() => import("./pages/UserDashboard/UserMainDashboard"));
const DepartmentSelection = lazy(() => import("./pages/UserDashboard/DepartmentSelection"));
const DepartmentCategories = lazy(() => import("./pages/UserDashboard/DepartmentCategories"));
const UserBookings = lazy(() => import("./pages/UserDashboard/UserBookings"));
const Support = lazy(() => import("./pages/UserDashboard/Support"));
const Membership = lazy(() => import("./pages/UserDashboard/Membership"));
const HostDashboard = lazy(() => import("./pages/HostDashboard/HostDashboard"));
const HostProperties = lazy(() => import("./pages/HostDashboard/HostProperties"));
const HostProfile = lazy(() => import("./pages/HostDashboard/HostProfile"));
const HostCouponsOffers = lazy(() => import("./pages/HostDashboard/HostCouponsOffers"));
const HostBookings = lazy(() => import("./pages/HostDashboard/HostBookings"));
const HostTodayArrivals = lazy(() => import("./pages/HostDashboard/HostTodayArrivals"));
const HostMembershipCards = lazy(() => import("./pages/HostDashboard/HostMembershipCards"));
const MembershipCardRequests = lazy(() => import("./pages/HostDashboard/MembershipCardRequests"));
const HostSubscription = lazy(() => import("./pages/HostDashboard/HostSubscription"));
const HostSubscriptionPlans = lazy(() => import("./pages/HostDashboard/HostSubscriptionPlans"));
const SubscriptionSelection = lazy(() => import("./pages/HostDashboard/SubscriptionSelection"));
const SubscriptionPayment = lazy(() => import("./pages/HostDashboard/SubscriptionPayment"));
const HostSubscriptionDetails = lazy(() => import("./pages/HostDashboard/HostSubscriptionDetails"));
const HostPropertyDetails = lazy(() => import("./pages/HostDashboard/HostPropertyDetails"));
const HostSupport = lazy(() => import("./pages/HostDashboard/HostSupport"));
const HostPOSBilling = lazy(() => import("./pages/HostDashboard/HostPOSBilling"));
const HostStockInward = lazy(() => import("./pages/HostDashboard/HostStockInward"));
const HostSuppliers = lazy(() => import("./pages/HostDashboard/HostSuppliers"));
const HostCategories = lazy(() => import("./pages/HostDashboard/HostCategories"));
const HostPOSInventory = lazy(() => import("./pages/HostDashboard/HostPOSInventory"));
const HostPOSReports = lazy(() => import("./pages/HostDashboard/HostPOSReports"));
const HostPOSBillingHistory = lazy(() => import("./pages/HostDashboard/HostPOSBillingHistory"));
const HostPOSSettings = lazy(() => import("./pages/HostDashboard/HostPOSSettings"));
const HostEmployeeManagement = lazy(() => import("./pages/HostDashboard/HostEmployeeManagementV2"));
const HostAddPropertyPage = lazy(() => import("./pages/HostDashboard/HostAddPropertyPage"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard/EmployeeDashboard"));
const EmployeePOSBilling = lazy(() => import("./pages/EmployeeDashboard/EmployeePOSBilling"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));

// USER BOOKING PAGES
const BookingSelection = lazy(() => import("./pages/UserDashboard/BookingSelection"));
const HospitalBooking = lazy(() => import("./pages/UserDashboard/HospitalBooking"));
const HotelBooking = lazy(() => import("./pages/UserDashboard/HotelBooking"));
const CabBooking = lazy(() => import("./pages/UserDashboard/CabBooking"));
const SalonBooking = lazy(() => import("./pages/UserDashboard/SalonBooking"));
const DynamicBooking = lazy(() => import("./pages/UserDashboard/DynamicBooking"));
const BookingPayment = lazy(() => import("./pages/UserDashboard/BookingPayment"));
const MembershipPayment = lazy(() => import("./pages/UserDashboard/MembershipPayment"));

// ADMIN SUB-PAGES
const AdminBookedHotels = lazy(() => import("./pages/AdminDashboard/AdminBookedHotels"));
const AdminHostsList = lazy(() => import("./pages/AdminDashboard/AdminHostsList"));
const AdminDepartments = lazy(() => import("./pages/AdminDashboard/AdminDepartments"));
const AdminDepartmentDetails = lazy(() => import("./pages/AdminDashboard/AdminDepartmentDetails"));
const AdminHostProperties = lazy(() => import("./pages/AdminDashboard/AdminHostProperties"));
const AdminUsersList = lazy(() => import("./pages/AdminDashboard/AdminUsersList"));
const AdminOffers = lazy(() => import("./pages/AdminDashboard/AdminOffers"));
const AdminCoupons = lazy(() => import("./pages/AdminDashboard/AdminCoupons"));
const AdminSubscriptionList = lazy(() => import("./pages/AdminDashboard/AdminSubscriptionList"));
const AdminSubscriptionRequests = lazy(() => import("./pages/AdminDashboard/AdminSubscriptionRequests"));
const AdminMembershipList = lazy(() => import("./pages/AdminDashboard/AdminMembershipList"));
const AdminSystemSettings = lazy(() => import("./pages/AdminDashboard/AdminSystemSettings"));
const AdminSmsSettings = lazy(() => import("./pages/AdminDashboard/AdminSmsSettings"));
const AppSettings = lazy(() => import("./pages/AdminDashboard/AppSettings"));
const AdminRedemptions = lazy(() => import("./pages/AdminDashboard/AdminRedemptions"));
const AdminTemplateManagement = lazy(() => import("./pages/AdminDashboard/AdminTemplateManagement"));
const AdminSupport = lazy(() => import("./pages/AdminDashboard/AdminSupport"));
const AdminPropertyDetails = lazy(() => import("./pages/AdminDashboard/AdminPropertyDetails"));
const AdminDepositHistory = lazy(() => import("./pages/AdminDashboard/AdminDepositHistory"));
const AdminBranchRequests = lazy(() => import("./pages/AdminDashboard/AdminBranchRequests"));
const AdminOnboardingSlides = lazy(() => import("./pages/AdminDashboard/AdminOnboardingSlides"));
const AdminSplashScreen = lazy(() => import("./pages/AdminDashboard/AdminSplashScreen"));
const AdminCategoryDivisions = lazy(() => import("./pages/AdminDashboard/AdminCategoryDivisions"));
const AdminDeposits = lazy(() => import("./pages/AdminDashboard/AdminDeposits"));
const RazorpaySettings = lazy(() => import("./pages/AdminDashboard/RazorpaySettings"));
const DirectUpiSettings = lazy(() => import("./pages/AdminDashboard/DirectUpiSettings"));

const UserOffers = lazy(() => import("./pages/UserDashboard/UserOffers"));
const UserMembershipRequest = lazy(() => import("./pages/UserDashboard/UserMembershipRequest"));
const UserMembershipCards = lazy(() => import("./pages/UserDashboard/UserMembershipCards"));
const UserReviews = lazy(() => import("./pages/UserDashboard/UserReviews"));
const HostReviews = lazy(() => import("./pages/HostDashboard/HostReviews"));
const HostNotifications = lazy(() => import("./pages/HostDashboard/HostNotifications"));
const AdminNotifications = lazy(() => import("./pages/AdminDashboard/AdminNotifications"));
const AdminSendNotification = lazy(() => import("./pages/AdminDashboard/AdminSendNotification"));
const AdminRefunds = lazy(() => import("./pages/AdminDashboard/AdminRefunds"));
const AdminReviewModeration = lazy(() => import("./pages/AdminDashboard/AdminReviewModeration"));
const AdminBookingDetails = lazy(() => import("./pages/AdminDashboard/AdminBookingDetails"));
const AdminWithdrawalRequests = lazy(() => import("./pages/AdminDashboard/AdminWithdrawalRequests"));
const AdminWallet = lazy(() => import("./pages/AdminDashboard/AdminWallet"));
const AdminDeletionRequests = lazy(() => import("./pages/AdminDashboard/AdminDeletionRequests"));
const HostBookingDetailsPage = lazy(() => import("./pages/HostDashboard/HostBookingDetailsPage"));
const UserPropertyDetailsPage = lazy(() => import("./pages/UserDashboard/UserPropertyDetailsPage"));
const UserBookingPage = lazy(() => import("./pages/UserDashboard/UserBookingPage"));
const UserNotifications = lazy(() => import("./pages/UserDashboard/UserNotifications"));
const UserWalletHistory = lazy(() => import("./pages/UserDashboard/UserWalletHistory"));
const RefundPolicy = lazy(() => import("./pages/UserDashboard/RefundPolicy"));
const AboutUs = lazy(() => import("./pages/UserDashboard/AboutUs"));
const TermsConditions = lazy(() => import("./pages/UserDashboard/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/UserDashboard/PrivacyPolicy"));
const UserProfile = lazy(() => import("./pages/UserDashboard/UserProfile"));
const HostWithdrawals = lazy(() => import("./pages/HostDashboard/HostWithdrawals"));
const NotFound = lazy(() => import("./pages/common/NotFound"));
const NewBizComparison = lazy(() => import("./pages/Landing/NewBizComparison"));

const HostRefundPolicy = lazy(() => import("./pages/HostDashboard/HostRefundPolicy"));
const HostAboutUs = lazy(() => import("./pages/HostDashboard/HostAboutUs"));
const HostTermsConditions = lazy(() => import("./pages/HostDashboard/HostTermsConditions"));
const HostPrivacyPolicy = lazy(() => import("./pages/HostDashboard/HostPrivacyPolicy"));
const HostCompanyChat = lazy(() => import("./pages/HostDashboard/HostCompanyChat"));
const HostRefunds = lazy(() => import("./pages/HostDashboard/HostRefunds"));


function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
}

function App() {
  useEffect(() => {
    // Only check redirect result if a redirect authentication flow is active (prevents loading Firebase on standard visits)
    const hasRedirectState = localStorage.getItem("google_auth_requested_role") || 
                             window.location.hash.includes("access_token") || 
                             window.location.search.includes("code=");
                             
    if (hasRedirectState) {
      import("./services/authService").then(({ handleAuthRedirect }) => {
        handleAuthRedirect().then(user => {
          if (user) {
            console.log("User logged in via redirect:", user);
            // Get the role from the service's stored hint or default to USER
            const role = localStorage.getItem("google_auth_role") || "USER";
            const dashboardPath = role === 'HOST' ? '/host-dashboard' : '/user/home';
            
            console.log(`[App] Redirecting to ${dashboardPath} after successful auth`);
            window.location.replace(dashboardPath);
          }
        }).catch(err => {
          console.error("Auth redirect error:", err);
        });
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <GlobalNotificationHandler />
      <ChatBot />
      <Suspense fallback={<PremiumLoading />}>
        <Routes>
        {/* ROLE SELECTION PAGE - Dynamic Landing Page Controller */}
        <Route path="/" element={<LandingPageController />} />
        
        {/* SEO Landing Pages */}
        <Route path="/compare-newbiz" element={<NewBizComparison />} />

        {/* USER ROUTES */}
        <Route path="/user-register" element={<UserRegister />} />
        <Route path="/user-login" element={<UserLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/user" element={<AuthGuard role="USER" guestAllowed={true}><UserHome /></AuthGuard>} />
        <Route path="/user/home" element={<AuthGuard role="USER" guestAllowed={true}><UserHome /></AuthGuard>} />
        <Route path="/user/choose-categories" element={<AuthGuard role="USER" guestAllowed={true}><DepartmentSelection /></AuthGuard>} />
        <Route path="/user/dashboard" element={<AuthGuard role="USER"><UserDashboard /></AuthGuard>} />
        <Route path="/user/main-dashboard" element={<AuthGuard role="USER"><UserMainDashboard /></AuthGuard>} />
        <Route path="/user/:department" element={<AuthGuard role="USER" guestAllowed={true}><DepartmentCategories /></AuthGuard>} />
        <Route path="/user/dashboard/:department" element={<AuthGuard role="USER"><UserDashboard /></AuthGuard>} />
        <Route path="/user/bookings" element={<AuthGuard role="USER"><UserBookings /></AuthGuard>} />
        <Route path="/user/support" element={<AuthGuard role="USER"><Support /></AuthGuard>} />
        <Route path="/user/membership" element={<AuthGuard role="USER"><Navigate to="/user/main-dashboard?tab=membership" replace /></AuthGuard>} />
        <Route path="/user/membership-request" element={<AuthGuard role="USER"><Navigate to="/user/main-dashboard?tab=membership" replace /></AuthGuard>} />
        <Route path="/user/membership-cards" element={<AuthGuard role="USER"><UserMembershipCards /></AuthGuard>} />
        <Route path="/user/reviews" element={<AuthGuard role="USER"><UserReviews /></AuthGuard>} />
        <Route path="/user/notifications" element={<AuthGuard role="USER"><UserNotifications /></AuthGuard>} />
        <Route path="/user/wallet-history" element={<AuthGuard role="USER"><UserWalletHistory /></AuthGuard>} />
        <Route path="/user/refund-policy" element={<RefundPolicy />} />
        <Route path="/user/about" element={<AboutUs />} />
        <Route path="/user/terms" element={<TermsConditions />} />
        <Route path="/user/privacy" element={<PrivacyPolicy />} />
        <Route path="/user/profile" element={<AuthGuard role="USER"><UserProfile /></AuthGuard>} />

        {/* TOP LEVEL BOOKING REDIRECTS (Handles /booking/Hospital, /booking/Hotel, etc.) */}
        <Route path="/booking" element={<Navigate to="/user/choose-categories" replace />} />
        <Route path="/booking/hotel" element={<Navigate to="/user/booking/hotel" replace />} />
        <Route path="/booking/hospital" element={<Navigate to="/user/booking/hospital" replace />} />
        <Route path="/booking/cab" element={<Navigate to="/user/booking/cab" replace />} />
        <Route path="/booking/salon" element={<Navigate to="/user/booking/salon" replace />} />
        <Route path="/booking/:department" element={<BookingDeptRedirect />} />
        <Route path="/booking/:department/:id" element={<BookingItemRedirect />} />

        {/* NEW BOOKING ROUTES - Specific routes MUST come before dynamic route */}
        <Route path="/user/booking-selection" element={<AuthGuard role="USER" guestAllowed={true}><BookingSelection /></AuthGuard>} />
        <Route path="/user/booking/hotel" element={<AuthGuard role="USER" guestAllowed={true}><HotelBooking /></AuthGuard>} />
        <Route path="/user/booking/hospital" element={<AuthGuard role="USER" guestAllowed={true}><HospitalBooking /></AuthGuard>} />
        <Route path="/user/booking/cab" element={<AuthGuard role="USER" guestAllowed={true}><CabBooking /></AuthGuard>} />
        <Route path="/user/booking/salon" element={<AuthGuard role="USER" guestAllowed={true}><SalonBooking /></AuthGuard>} />
        {/* Dynamic route for custom departments - catches all other /user/booking/* routes */}
        <Route path="/user/booking/:department" element={<AuthGuard role="USER" guestAllowed={true}><DynamicBooking /></AuthGuard>} />
        <Route path="/user/booking-payment" element={<AuthGuard role="USER"><BookingPayment /></AuthGuard>} />
        <Route path="/user/membership-payment" element={<AuthGuard role="USER"><MembershipPayment /></AuthGuard>} />

        {/* HOST ROUTES */}
        <Route path="/host-register" element={<HostRegister />} />
        <Route path="/host-login" element={<HostLogin />} />
        <Route path="/host/dashboard" element={<AuthGuard role="HOST"><HostDashboard /></AuthGuard>} />
        <Route path="/host-dashboard" element={<AuthGuard role="HOST"><HostDashboard /></AuthGuard>} />
        <Route path="/host-dashboard/create-property" element={<AuthGuard role="HOST"><HostAddPropertyPage /></AuthGuard>} />
        <Route path="/host/create-property" element={<AuthGuard role="HOST"><HostAddPropertyPage /></AuthGuard>} />
        <Route path="/host/properties" element={<AuthGuard role="HOST"><HostProperties /></AuthGuard>} />
        <Route path="/host/property/:id" element={<AuthGuard role="HOST"><HostPropertyDetails /></AuthGuard>} />
        <Route path="/host/profile" element={<AuthGuard role="HOST"><HostProfile /></AuthGuard>} />
        <Route path="/host/coupons-offers" element={<AuthGuard role="HOST"><HostCouponsOffers /></AuthGuard>} />
        <Route path="/host/bookings" element={<AuthGuard role="HOST"><HostBookings /></AuthGuard>} />
        <Route path="/host/today-arrivals" element={<AuthGuard role="HOST"><HostTodayArrivals /></AuthGuard>} />
        <Route path="/host/reviews" element={<AuthGuard role="HOST"><HostReviews /></AuthGuard>} />
        <Route path="/host/membership-cards" element={<AuthGuard role="HOST"><HostMembershipCards /></AuthGuard>} />
        <Route path="/host/subscription" element={<AuthGuard role="HOST"><SubscriptionSelection /></AuthGuard>} />
        <Route path="/host-subscription" element={<AuthGuard role="HOST"><SubscriptionSelection /></AuthGuard>} />
        <Route path="/host/subscription-payment" element={<AuthGuard role="HOST"><SubscriptionPayment /></AuthGuard>} />
        <Route path="/host/subscription-plans" element={<AuthGuard role="HOST"><HostSubscriptionPlans /></AuthGuard>} />
        <Route path="/host/subscription-details" element={<AuthGuard role="HOST"><HostSubscriptionDetails /></AuthGuard>} />
        <Route path="/host/support" element={<AuthGuard role="HOST"><HostSupport /></AuthGuard>} />
        <Route path="/host/notifications" element={<AuthGuard role="HOST"><HostNotifications /></AuthGuard>} />
        <Route path="/host/withdrawals" element={<AuthGuard role="HOST"><HostWithdrawals /></AuthGuard>} />
        <Route path="/host/pos" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSBilling /></FeatureGuard></AuthGuard>} />
        <Route path="/host/pos/inventory" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSInventory /></FeatureGuard></AuthGuard>} />
        <Route path="/host/refund-requests" element={<AuthGuard role="HOST"><HostRefunds /></AuthGuard>} />

        <Route path="/host/refund-policy" element={<AuthGuard role="HOST"><HostRefundPolicy /></AuthGuard>} />
        <Route path="/host/about" element={<AuthGuard role="HOST"><HostAboutUs /></AuthGuard>} />
        <Route path="/host/terms" element={<AuthGuard role="HOST"><HostTermsConditions /></AuthGuard>} />
        <Route path="/host/privacy" element={<AuthGuard role="HOST"><HostPrivacyPolicy /></AuthGuard>} />

        {/* Inventory Module Routes (Support both /host/pos/... and /host/inventory/...) */}
        <Route path="/host/inventory/all-products" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSInventory /></FeatureGuard></AuthGuard>} />
        <Route path="/host/inventory/stock-inward" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostStockInward /></FeatureGuard></AuthGuard>} />
        <Route path="/host/inventory/suppliers" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostSuppliers /></FeatureGuard></AuthGuard>} />
        <Route path="/host/inventory/categories" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostCategories /></FeatureGuard></AuthGuard>} />

        <Route path="/host/pos/stock-inward" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostStockInward /></FeatureGuard></AuthGuard>} />
        <Route path="/host/pos/suppliers" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostSuppliers /></FeatureGuard></AuthGuard>} />
        <Route path="/host/pos/categories" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostCategories /></FeatureGuard></AuthGuard>} />

        <Route path="/host/pos/reports" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSReports /></FeatureGuard></AuthGuard>} />
        <Route path="/host/pos/billing-history" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSBillingHistory /></FeatureGuard></AuthGuard>} />
        <Route path="/host/pos/settings" element={<AuthGuard role="HOST"><FeatureGuard feature="BILLING_POS"><HostPOSSettings /></FeatureGuard></AuthGuard>} />
        <Route path="/host/employee-management" element={<AuthGuard role="HOST"><FeatureGuard feature="EMPLOYEE_MANAGEMENT"><HostEmployeeManagement /></FeatureGuard></AuthGuard>} />
        <Route path="/host/company-chat" element={<AuthGuard role="HOST"><HostCompanyChat /></AuthGuard>} />


        <Route path="/host/membership-requests" element={<AuthGuard role="HOST"><MembershipCardRequests /></AuthGuard>} />
        <Route path="/host/booking-details/:department/:id" element={<AuthGuard role="HOST"><HostBookingDetailsPage /></AuthGuard>} />
        <Route path="/user/property-details/:department/:id" element={<UserPropertyDetailsPage />} />
        <Route path="/user/booking/:department/:id" element={<UserBookingPage />} />

        {/* ADMIN ROUTES */}
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/employee-login" element={<EmployeeLogin />} />
        <Route path="/employee/dashboard" element={<FeatureGuard feature="EMPLOYEE_MANAGEMENT"><EmployeeDashboard /></FeatureGuard>} />
        <Route path="/employee/pos" element={<FeatureGuard feature="BILLING_POS"><EmployeePOSBilling /></FeatureGuard>} />
        <Route path="/employee/inventory/all-products" element={<FeatureGuard feature="BILLING_POS"><HostPOSInventory /></FeatureGuard>} />
        <Route path="/employee/inventory/stock-inward" element={<FeatureGuard feature="BILLING_POS"><HostStockInward /></FeatureGuard>} />
        <Route path="/employee/inventory/suppliers" element={<FeatureGuard feature="BILLING_POS"><HostSuppliers /></FeatureGuard>} />
        <Route path="/employee/inventory/categories" element={<FeatureGuard feature="BILLING_POS"><HostCategories /></FeatureGuard>} />

        <Route path="/employee/pos/stock-inward" element={<FeatureGuard feature="BILLING_POS"><HostStockInward /></FeatureGuard>} />
        <Route path="/employee/pos/suppliers" element={<FeatureGuard feature="BILLING_POS"><HostSuppliers /></FeatureGuard>} />
        <Route path="/employee/pos/categories" element={<FeatureGuard feature="BILLING_POS"><HostCategories /></FeatureGuard>} />
        <Route path="/admin/dashboard" element={<AuthGuard role="ADMIN"><AdminDashboard /></AuthGuard>} />

        <Route path="/admin/booked-hotels" element={<AuthGuard role="ADMIN"><AdminBookedHotels /></AuthGuard>} />
        <Route path="/admin/hosts" element={<AuthGuard role="ADMIN"><AdminHostsList /></AuthGuard>} />
        <Route
          path="/admin/host-properties"
          element={<AuthGuard role="ADMIN"><AdminHostProperties /></AuthGuard>}
        />
        <Route path="/admin/branch-requests" element={<AuthGuard role="ADMIN"><AdminBranchRequests /></AuthGuard>} />
        <Route path="/admin/property/:id" element={<AuthGuard role="ADMIN"><AdminPropertyDetails /></AuthGuard>} />
        <Route path="/admin/property/:id/deposit-history" element={<AuthGuard role="ADMIN"><AdminDepositHistory /></AuthGuard>} />
        <Route path="/admin/departments" element={<AuthGuard role="ADMIN"><AdminDepartments /></AuthGuard>} />
        <Route path="/admin/deposits" element={<AuthGuard role="ADMIN"><AdminDeposits /></AuthGuard>} />
        <Route path="/admin/category-divisions" element={<AuthGuard role="ADMIN"><AdminCategoryDivisions /></AuthGuard>} />
        <Route path="/admin/department-details" element={<AuthGuard role="ADMIN"><AdminDepartmentDetails /></AuthGuard>} />
        <Route path="/admin/properties/:department" element={<AuthGuard role="ADMIN"><AdminDepartments /></AuthGuard>} />
        <Route path="/admin/users" element={<AuthGuard role="ADMIN"><AdminUsersList /></AuthGuard>} />
        <Route path="/admin/coupons" element={<AuthGuard role="ADMIN"><AdminCoupons /></AuthGuard>} />
        <Route path="/admin/offers" element={<AuthGuard role="ADMIN"><AdminOffers /></AuthGuard>} />
        <Route path="/admin/subscriptions" element={<AuthGuard role="ADMIN"><AdminSubscriptionList /></AuthGuard>} />
        <Route path="/admin/subscription-requests" element={<AuthGuard role="ADMIN"><AdminSubscriptionRequests /></AuthGuard>} />
        <Route path="/admin/memberships" element={<AuthGuard role="ADMIN"><AdminMembershipList /></AuthGuard>} />
        <Route path="/admin/support" element={<AuthGuard role="ADMIN"><AdminSupport /></AuthGuard>} />
        <Route path="/admin/notifications" element={<AuthGuard role="ADMIN"><AdminNotifications /></AuthGuard>} />
        <Route path="/admin/send-notification" element={<AuthGuard role="ADMIN"><AdminSendNotification /></AuthGuard>} />
        <Route path="/admin/refunds" element={<AuthGuard role="ADMIN"><AdminRefunds /></AuthGuard>} />
        <Route path="/admin/templates" element={<AuthGuard role="ADMIN"><AdminTemplateManagement /></AuthGuard>} />
        <Route path="/admin/system-settings" element={<AuthGuard role="ADMIN"><AdminSystemSettings /></AuthGuard>} />
        <Route path="/admin/sms-settings" element={<AuthGuard role="ADMIN"><AdminSmsSettings /></AuthGuard>} />
        <Route path="/admin/app-settings" element={<AuthGuard role="ADMIN"><AppSettings /></AuthGuard>} />
        <Route path="/admin/onboarding-slides" element={<AuthGuard role="ADMIN"><AdminOnboardingSlides /></AuthGuard>} />
        <Route path="/admin/splash-screen" element={<AuthGuard role="ADMIN"><AdminSplashScreen /></AuthGuard>} />
        <Route path="/admin/redemptions" element={<AuthGuard role="ADMIN"><AdminRedemptions /></AuthGuard>} />
        <Route path="/admin/reviews" element={<AuthGuard role="ADMIN"><AdminReviewModeration /></AuthGuard>} />

        <Route path="/admin/bookings/:id" element={<AuthGuard role="ADMIN"><AdminBookingDetails /></AuthGuard>} />
        <Route path="/admin/withdrawals" element={<AuthGuard role="ADMIN"><AdminWithdrawalRequests /></AuthGuard>} />
        <Route path="/admin/wallet" element={<AuthGuard role="ADMIN"><AdminWallet /></AuthGuard>} />
        <Route path="/admin/deletion-requests" element={<AuthGuard role="ADMIN"><AdminDeletionRequests /></AuthGuard>} />
        <Route path="/admin/payment-settings" element={<AuthGuard role="ADMIN"><RazorpaySettings /></AuthGuard>} />
        <Route path="/admin/direct-upi-settings" element={<AuthGuard role="ADMIN"><DirectUpiSettings /></AuthGuard>} />
        <Route path="/user/offers" element={<UserOffers />} />

        
        {/* CATCH-ALL 404 PAGE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
